import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BACKEND_URL } from "./api";

/** SEO batch — per-route head management with NO new dependency.
 * One component mounted inside <BrowserRouter>; on every route change it sets
 * document.title, meta description, meta robots and (public routes only) the
 * canonical link. Canonical is ALWAYS the apex production origin so the www
 * variant and the preview domain can never end up declared canonical.
 * Staff / corp / QR-session / tokened routes carry noindex so thin app screens
 * drop out of the crawl instead of being padded with copy. */

const ORIGIN = "https://inn-dulge.co.za";

const DEFAULT = {
  title: "Inn-dulGe — Go on. Inn-dulge.",
  description: "Inn-dulGe — order from the restaurant, your hotel room, or the office park. Holiday Inn Express Sandton-Woodmead.",
  noindex: true, // unmatched routes are tokened/private surfaces
};

// First match wins. `exact` matches the pathname exactly; otherwise prefix match.
const ROUTES = [
  {
    path: "/", exact: true, canonical: "/",
    title: "Inn-dulGe — Restaurant at Holiday Inn Express Sandton-Woodmead",
    description: "Inn-dulGe is the restaurant at the Holiday Inn Express Sandton-Woodmead, Woodmead Drive, Sandton. Dine in, hotel room service and office-park collection or delivery — open 12:00–22:00. No sign-up needed to order.",
  },
  {
    path: "/menu", canonical: "/menu",
    title: "Menu — Inn-dulGe",
    description: "Browse the full Inn-dulGe menu — starters, salads, grills, burgers, pizzas, pasta, seafood and desserts at the Holiday Inn Express Sandton-Woodmead. VAT-inclusive prices in rand.",
  },
  {
    path: "/privacy", canonical: "/privacy",
    title: "Privacy Notice — Inn-dulGe",
    description: "How Inn-dulGe at the Holiday Inn Express Sandton-Woodmead collects, uses and protects your personal information under POPIA — payment processing, retention and your data-subject rights.",
  },
  {
    path: "/staff/portal", noindex: true,
    title: "Staff portal — Inn-dulGe",
    description: "Inn-dulGe staff service portal.",
  },
  {
    path: "/staff", noindex: true,
    title: "Staff sign-in — Inn-dulGe",
    description: "Staff access to the Inn-dulGe service portals.",
  },
  {
    path: "/corp", noindex: true,
    title: "Corporate portal — Inn-dulGe",
    description: "Corporate account portal for Inn-dulGe at the Holiday Inn Express Sandton-Woodmead.",
  },
  { path: "/t/", noindex: true, title: "Table ordering — Inn-dulGe", description: "Order and watch your table bill live at Inn-dulGe." },
  { path: "/check/", noindex: true, title: "Table ordering — Inn-dulGe", description: "Order and watch your table bill live at Inn-dulGe." },
  { path: "/scan", noindex: true, title: "Table ordering — Inn-dulGe", description: "Scan your table QR to order at Inn-dulGe." },
];

function upsertMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const JSONLD_ID = "inndulge-jsonld";
let _jsonldCache = null;

function setJsonLd(enabled) {
  const existing = document.getElementById(JSONLD_ID);
  if (!enabled) {
    if (existing) existing.remove();
    return;
  }
  const inject = (text) => {
    let el = document.getElementById(JSONLD_ID);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = JSONLD_ID;
      document.head.appendChild(el);
    }
    el.textContent = text;
  };
  if (_jsonldCache) { inject(_jsonldCache); return; }
  fetch(`${BACKEND_URL}/api/seo/structured-data.json`)
    .then((r) => (r.ok ? r.text() : null))
    .then((text) => { if (text) { _jsonldCache = text; inject(text); } })
    .catch(() => {});
}

export function applySeo(pathname) {
  const r = ROUTES.find((c) => (c.exact ? pathname === c.path : pathname.startsWith(c.path))) || DEFAULT;
  document.title = r.title;
  upsertMeta("description", r.description);
  upsertMeta("robots", r.noindex ? "noindex, nofollow" : "index, follow");
  setCanonical(r.canonical ? ORIGIN + r.canonical : null);
  // schema.org JSON-LD (Restaurant + Menu) only on the indexable public pages
  setJsonLd(!r.noindex);
}

export default function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => { applySeo(pathname); }, [pathname]);
  return null;
}
